import { Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import type { AnnieChatItem } from "@/hooks/useAnnie";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { JumpingDots } from "@/ui/Animations/JumpingDots";
import { ChatCard } from "@/ui/Chat/ChatCard";
import { SystemChoiceList } from "@/ui/Clickable/SystemChoiceList";
import { SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { type AnnieClarificationOption, type AnniePickupRow, TIME_RANGE_SLOT } from "@/utils/annieStream";
import { AnniePickupTable } from "./AnniePickupTable";
import { AnnieTimeRangeCard } from "./AnnieTimeRangeCard";

type AnnieClarificationItem = Extract<AnnieChatItem, { kind: "clarification" }>;

type Props = {
	item: AnnieClarificationItem;
	onChoose: (option: AnnieClarificationOption) => void;
	onSubmitTimes: (values: { start_time: string; end_time: string }) => void;
};

/**
 * Past this many options the chip row becomes a wall inside the narrow chat window, so the picker
 * switches to a filterable dropdown. Slot-agnostic on purpose: reason lists are department config
 * (~11 entries) and location/job lists grow with the department, so keying off the COUNT keeps
 * every picker readable instead of special-casing one slot.
 */
const MAX_CHIP_OPTIONS = 6;

/**
 * A chip is a compact single-line pill; past this label length one chip alone overflows the narrow chat
 * card (and truncating hides the tail — shift labels differ only in their trailing time). Such options
 * render instead as a full-width wrapping choice list so every label stays fully readable.
 */
const MAX_CHIP_LABEL_LENGTH = 30;

/**
 * A disambiguation question from Annie ("Which Amir?") answered by picking one
 * option — labels are visible, the backing ids stay hidden in the option value.
 * Short lists render as chips (one tap); longer ones as a filterable dropdown.
 * A time clarification (Annie asking for shift hours) delegates to
 * AnnieTimeRangeCard.
 */
export const AnnieClarificationCard: FC<Props> = ({ item, onChoose, onSubmitTimes }) => {
	const { t, i18n } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { card, isChoosing, isExpired, chosenOptionId } = item;

	// `slot === "times"` is the transport-independent time-picker signal (survives SSE / replan / resume).
	if (card.slot === TIME_RANGE_SLOT) {
		return <AnnieTimeRangeCard item={item} onSubmitTimes={onSubmitTimes} />;
	}

	// Some labels are i18n keys (e.g. "annie.picker.skip", "annie.replacement.yes"); entity labels
	// (nurse names, department call-in reasons) come verbatim and pass through untranslated.
	const labelOf = (option: AnnieClarificationOption) => (i18n.exists(option.label) ? t(option.label) : option.label);
	const isDisabled = isChoosing || isExpired;

	// The approve nurse-picker is detected STRUCTURALLY (options carry the pickup metrics, mirroring how the
	// time-range picker is detected by shape — there is no verb on the card): render it AS the selectable tiny
	// table so the head nurse sees each pickup's cost/OT/partial, and picking a row round-trips via /replan.
	const isTablePicker = card.options.length > 0 && card.options.every((option) => option.pickup !== undefined);
	if (isTablePicker) {
		const rows: AnniePickupRow[] = card.options.map((option) => ({
			// biome-ignore lint/style/noNonNullAssertion: every() above guarantees pickup is present on each option.
			...option.pickup!,
			nurse_id: Number(option.id),
			nurse_label: labelOf(option),
		}));
		return (
			<ChatCard dataTestId={`annie-clarification-card-${card.clarification_id}`} dimmed={isExpired}>
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.5, color: INK.PRIMARY }}>{card.question}</Typography>
				<AnniePickupTable
					dataTestId={`annie-clarification-options-${card.clarification_id}`}
					rows={rows}
					timezone={chosenDepartment.timezone}
					selectionDisabled={isDisabled}
					onSelect={(nurseId) => {
						const chosen = card.options.find((option) => option.id === String(nurseId));
						if (chosen) onChoose(chosen);
					}}
				/>
				{isChoosing && <JumpingDots sx={{ justifyContent: "flex-start" }} />}
			</ChatCard>
		);
	}

	// Long labels overflow a chip row and get truncated in the dropdown — render them as a full-width
	// wrapping choice list so every option stays readable (takes precedence over the count-based split).
	const hasLongOption = card.options.some((option) => labelOf(option).length > MAX_CHIP_LABEL_LENGTH);
	if (hasLongOption) {
		return (
			<ChatCard dataTestId={`annie-clarification-card-${card.clarification_id}`} dimmed={isExpired}>
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.5, color: INK.PRIMARY }}>{card.question}</Typography>
				<SystemChoiceList
					dataTestId={`annie-clarification-options-${card.clarification_id}`}
					ariaLabel={card.question}
					disabled={isDisabled}
					items={card.options.map((option) => ({
						value: option.id,
						label: labelOf(option),
						dataTestId: `annie-clarification-option-${option.id}`,
					}))}
					onSelect={(id) => {
						const chosen = card.options.find((option) => option.id === id);
						if (chosen) onChoose(chosen);
					}}
				/>
				{isChoosing && <JumpingDots sx={{ justifyContent: "flex-start" }} />}
			</ChatCard>
		);
	}

	return (
		<ChatCard dataTestId={`annie-clarification-card-${card.clarification_id}`} dimmed={isExpired}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, lineHeight: 1.5, color: INK.PRIMARY }}>{card.question}</Typography>
			{card.options.length > MAX_CHIP_OPTIONS ? (
				<SystemDropdown<string>
					data-testid={`annie-clarification-options-${card.clarification_id}`}
					items={card.options.map((option) => ({ value: option.id, label: labelOf(option) }))}
					selectedItem={chosenOptionId}
					// Selecting IS the answer — same single interaction as tapping a chip, no confirm step.
					onChange={(id) => {
						const chosen = card.options.find((option) => option.id === id);
						if (chosen) onChoose(chosen);
					}}
					label={t("annie.clarification.select")}
					showFilter
					fullWidth
					disabled={isDisabled}
				/>
			) : (
				<SystemChips<AnnieClarificationOption>
					dataTestId={`annie-clarification-options-${card.clarification_id}`}
					items={card.options.map((option) => ({ value: option, label: labelOf(option), disabled: isDisabled }))}
					selectedItems={[]}
					onChange={(chip) => onChoose(chip.value)}
					forceFitContent
					touchTarget
					rootSx={{ justifyContent: "flex-start" }}
					getItemDataTestId={(chip) => `annie-clarification-option-${chip.value.id}`}
				/>
			)}
			{isChoosing && <JumpingDots sx={{ justifyContent: "flex-start" }} />}
		</ChatCard>
	);
};
